import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Icon,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { fieldStyles } from "services/utils";

const SuccessionPlanTrainingUserDialog = ({ open, onClose, plan }) => {
  const { t } = useTranslation();

  // State to track which step is selected in the dropdown
  const [selectedStepId, setSelectedStepId] = useState("");

  // Set the first step as default when the dialog opens
  useEffect(() => {
    if (open && plan?.steps?.length > 0) {
      setSelectedStepId(plan.steps[0].id);
    }
  }, [open, plan]);

  // Find the full data for the currently selected step
  const currentStep = plan?.steps?.find((s) => s.id === selectedStepId);
  const trainings = currentStep?.trainings || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", p: 1, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
      }}
    >
      {/* HEADER SECTION */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#e3f2fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
            }}
          >
            <Icon sx={{ color: "#1976d2" }}>school</Icon>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#344767", lineHeight: 1.2 }}>
              {t("admin.successionPlanSteps.trainings")}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 1 }}>
        {/* STEP SELECTOR DROPDOWN */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <TextField
            select
            fullWidth
            label={t("admin.successionPlanSteps.step")}
            value={selectedStepId}
            onChange={(e) => setSelectedStepId(e.target.value)}
            size="small"
            sx={fieldStyles}
          >
            {plan?.steps?.map((step, index) => (
              <MenuItem key={step.id} value={step.id}>
                {t("admin.successionPlanSteps.step")} {index + 1}: {step.jobName}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography variant="button" fontWeight="bold" color="text" sx={{ display: "block" }}>
          {t("admin.successionPlanSteps.tr")}: {trainings.length}
        </Typography>

        <Divider sx={{ mb: 1 }} />

        {/* TRAININGS LIST */}
        <List disablePadding key={selectedStepId}>
          {trainings.length > 0 ? (
            trainings.map((tr) => (
              <ListItem key={`${selectedStepId}-${tr.id}`} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: "35px" }}>
                  {tr.finished ? (
                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "20px" }} />
                  ) : (
                    <ErrorOutlineIcon sx={{ color: "#fb8c00", fontSize: "20px" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={tr.trainingName}
                  secondary={
                    tr.finished
                      ? t("admin.successionPlanSteps.completed")
                      : t("admin.successionPlanSteps.pending")
                  }
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: "bold",
                    color: "#344767",
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: tr.finished ? "success.main" : "warning.main",
                  }}
                />
              </ListItem>
            ))
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Icon sx={{ fontSize: "40px !important", color: "#ddd", mb: 1 }}>auto_stories</Icon>
              <Typography variant="body2" color="text.disabled">
                {t("admin.successionPlanSteps.noTr")}
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            px: 4,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": { bgcolor: "#1565c0" },
          }}
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SuccessionPlanTrainingUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  plan: PropTypes.shape({
    targetJobName: PropTypes.string,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        jobName: PropTypes.string,
        trainings: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            trainingName: PropTypes.string,
            finished: PropTypes.bool,
          })
        ),
      })
    ),
  }),
};

export default SuccessionPlanTrainingUserDialog;
